import { getInfocardDetail } from "./details.js";

function requireElement(root, selector) {
  const element = root.querySelector(selector);
  if (!element) {
    throw new Error(`Missing infocard modal element: ${selector}`);
  }
  return element;
}

export function createInfocardModalController({
  targetDocument = document,
  getDetail = getInfocardDetail,
} = {}) {
  const dialog = requireElement(targetDocument, ".infocard-modal");
  const title = requireElement(dialog, ".infocard-modal__title");
  const scrollArea = requireElement(dialog, ".infocard-modal__scroll");
  const explanation = requireElement(
    dialog,
    ".infocard-modal__explanation",
  );
  const facts = requireElement(dialog, ".infocard-modal__facts");
  const closeButton = requireElement(dialog, ".infocard-modal__close");
  const cards = Array.from(
    targetDocument.querySelectorAll(".infocard__item"),
  );
  const triggerBindings = [];
  let returnFocus = null;

  function render(detail, titleText) {
    title.textContent = titleText;

    const paragraphs = detail.explanation.map((paragraphText) => {
      const paragraph = targetDocument.createElement("p");
      paragraph.textContent = paragraphText;
      return paragraph;
    });
    explanation.replaceChildren(...paragraphs);

    const rows = detail.facts.map(({ label, value }) => {
      const row = targetDocument.createElement("div");
      const term = targetDocument.createElement("dt");
      const description = targetDocument.createElement("dd");
      row.className = "infocard-modal__fact";
      term.textContent = label;
      description.textContent = value;
      row.append(term, description);
      return row;
    });
    facts.replaceChildren(...rows);
  }

  function open(cardName, titleText, trigger) {
    const detail = getDetail(cardName);
    if (!detail) return false;

    render(detail, titleText);
    returnFocus = trigger ?? targetDocument.activeElement;
    scrollArea.scrollTop = 0;

    if (!dialog.open) dialog.showModal();
    closeButton.focus();
    return true;
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  function handleClose() {
    const focusTarget = returnFocus;
    returnFocus = null;
    if (focusTarget?.isConnected !== false) focusTarget?.focus?.();
  }

  function handleCancel(event) {
    event.preventDefault();
    close();
  }

  function handleDialogClick(event) {
    if (event.target === dialog) close();
  }

  closeButton.addEventListener("click", close);
  dialog.addEventListener("cancel", handleCancel);
  dialog.addEventListener("close", handleClose);
  dialog.addEventListener("click", handleDialogClick);

  cards.forEach((card) => {
    const trigger = card.querySelector(".infocard__cta");
    if (!trigger) return;

    const handleTriggerClick = () => {
      const heading = card.querySelector("h3");
      open(card.dataset.card, heading?.textContent?.trim() ?? "", trigger);
    };

    trigger.addEventListener("click", handleTriggerClick);
    triggerBindings.push({ trigger, handleTriggerClick });
  });

  return {
    open,
    close,
    destroy() {
      triggerBindings.forEach(({ trigger, handleTriggerClick }) => {
        trigger.removeEventListener("click", handleTriggerClick);
      });
      closeButton.removeEventListener("click", close);
      dialog.removeEventListener("cancel", handleCancel);
      dialog.removeEventListener("close", handleClose);
      dialog.removeEventListener("click", handleDialogClick);
      returnFocus = null;
      if (dialog.open) dialog.close();
    },
  };
}
